import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Printer, RefreshCw, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const getDeliveryAddress = (order) => {
  const address = order?.CustomerAddress || {};
  const emirate = address.Emirate?.Name;
  const location = address.Location?.Name;
  const area = address.Area?.Name || address.Location?.Area?.Name;
  return [emirate, location, area].filter(Boolean).join(', ') || '-';
};

const BatchPrint = () => {
  const [batchNo, setBatchNo] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const track = async () => {
    const trimmed = batchNo.trim();
    if (!trimmed) {
      setError('Enter Batch No');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetOrdersByBatchNo(trimmed);
      setOrders(parseList(response));
    } catch (err) {
      console.error('Get orders by batch no error:', err);
      setOrders([]);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const printOrder = async () => {
    const trimmed = batchNo.trim();
    if (!trimmed) {
      setError('Enter Batch No');
      return;
    }

    try {
      setPrinting(true);
      setError('');
      const response = await MainService.PrintByBatchNo(trimmed);
      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank', 'noopener,noreferrer');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Print by batch no error:', err);
      setError('Print Error');
    } finally {
      setPrinting(false);
    }
  };

  const reset = () => {
    setBatchNo('');
    setOrders([]);
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Batch Print</h1>
          <p className="mt-1 text-sm text-gray-500">Find orders by batch number and print the batch order manifesto.</p>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row">
          <input
            type="text"
            value={batchNo}
            onChange={(event) => setBatchNo(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') track();
            }}
            placeholder="Batch No"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 lg:max-w-md"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={track}
              disabled={loading || printing}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              Get Orders
            </button>
            <button
              type="button"
              onClick={printOrder}
              disabled={loading || printing}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={loading || printing}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Orders</h2>
            <p className="text-sm text-gray-500">{orders.length} orders found</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1100px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Voucher No</Th>
                <Th>Client</Th>
                <Th>Customer</Th>
                <Th>Delivery Address</Th>
                <Th>Delivery Date</Th>
                <Th>Product Price</Th>
                <Th>Delivery Charge</Th>
                <Th>Client Payment</Th>
                <Th>Collection Payment</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={10} text="Loading orders..." />
              ) : orders.length === 0 ? (
                <EmptyRow colSpan={10} text="No orders found." />
              ) : (
                orders.map((order) => (
                  <tr key={order.Id} className="hover:bg-gray-50">
                    <Td strong>{order.VoucherNo}</Td>
                    <Td>{order.Client?.Name || '-'}</Td>
                    <Td>{order.Customer?.Name || '-'}</Td>
                    <Td>{getDeliveryAddress(order)}</Td>
                    <Td>{formatDateTime(order.DeliveryDate)}</Td>
                    <Td>{formatAmount(order.ProductPrice)}</Td>
                    <Td>{formatAmount(order.DeliveryCharge)}</Td>
                    <Td>{formatAmount(order.ClientAmount)}</Td>
                    <Td>{formatAmount(order.CustomerAmount)}</Td>
                    <Td>{order.Status?.Name || order.StatusName || '-'}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children }) => (
  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
    {children}
  </th>
);

const Td = ({ children, strong = false }) => (
  <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
    {children}
  </td>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
      {text}
    </td>
  </tr>
);

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default BatchPrint;
